/**
 * TanStack Query infinite-query hook for the agent free-text search.
 *
 * Wraps GET /api/v1/agent/tickets/search (Phase 10 Branch B). The
 * endpoint returns a cursor-paginated envelope so we use
 * useInfiniteQuery, reading next_cursor from each page to advance.
 *
 * Disabled when q is empty so the inbox falls back to the list hook
 * without a redundant request. 30s staleTime so flipping a sidebar
 * filter does not re-fetch every time.
 */
import { useInfiniteQuery, type UseInfiniteQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { SearchPageResponse } from "@/types/api";

/** Allowlisted sort-by values for the agent search endpoint. Mirrors
 * backend SEARCH_SORT_FIELDS - wider than the list endpoint because
 * "score" (the default relevance order) is valid here. */
export type SearchSortBy =
  | "score"
  | "ticket_number"
  | "status"
  | "priority"
  | "requester_name"
  | "subject"
  | "assignee_name"
  | "company_name"
  | "last_activity_at";

export type SortOrder = "asc" | "desc";

export interface UseAgentTicketSearchFilters {
  status?: string;
  priority?: string;
  type?: string;
  assignee?: string;
  company_id?: string;
  assignee_email?: string;
  since?: string;
  until?: string;
  sort_by?: SearchSortBy;
  sort_order?: SortOrder;
}

export function agentSearchQueryKey(
  q: string,
  filters: UseAgentTicketSearchFilters,
  limit: number,
): readonly unknown[] {
  // Stringify the filter shape so the query key is stable. The hook
  // returns disabled when q is blank, but we still include q in the
  // key so the cache buckets per search term.
  return [
    "agent",
    "tickets",
    "search",
    q,
    filters.status ?? "",
    filters.priority ?? "",
    filters.type ?? "",
    filters.assignee ?? "",
    filters.company_id ?? "",
    filters.assignee_email ?? "",
    filters.since ?? "",
    filters.until ?? "",
    filters.sort_by ?? "score",
    filters.sort_order ?? "desc",
    limit,
  ] as const;
}

export function useAgentTicketSearch(
  q: string,
  filters: UseAgentTicketSearchFilters = {},
  limit = 25,
): UseInfiniteQueryResult<{ pages: SearchPageResponse[]; pageParams: (string | null)[] }, Error> {
  const trimmed = q.trim();
  return useInfiniteQuery({
    queryKey: agentSearchQueryKey(trimmed, filters, limit),
    enabled: trimmed.length > 0,
    staleTime: 30_000,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = { q: trimmed, limit: String(limit) };
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.type) params.type = filters.type;
      if (filters.assignee) params.assignee = filters.assignee;
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.assignee_email) params.assignee_email = filters.assignee_email;
      if (filters.since) params.since = filters.since;
      if (filters.until) params.until = filters.until;
      if (filters.sort_by) params.sort_by = filters.sort_by;
      if (filters.sort_order) params.sort_order = filters.sort_order;
      if (pageParam) params.cursor = pageParam;
      const response = await api.get<SearchPageResponse>(
        "/agent/tickets/search",
        { params },
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}
